import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPostBySlug } from '../../data/blogPosts';
import ScienceIcon from '@mui/icons-material/Science';
import { useSEO } from '../../hooks/useSEO';

const SHARE_LINKS = [
  { name: 'LinkedIn', icon: 'M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 9v-5.5h-2.29v-2.5h2.29V9.5h2v2h2.5v2H21V17h-2v1.5h-2.5m-5-7.5a2.5 2.5 0 0 0-2.5 2.5v5a2.5 2.5 0 0 0 5 0v-5a2.5 2.5 0 0 0-2.5-2.5z', href: 'https://www.linkedin.com/shareArticle' },
  { name: 'WhatsApp', icon: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z', href: 'https://wa.me' },
  { name: 'X', icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z', href: 'https://twitter.com/intent/tweet' },
  { name: 'Facebook', icon: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z', href: 'https://www.facebook.com/sharer/sharer.php' },
  { name: 'Email', icon: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z', href: 'mailto:' },
];

function stripStars(s: string) {
  return s.replace(/\*\*(.*?)\*\*/g, '$1');
}

function formatBody(text: string) {
  const blocks = text.split('\n\n');
  const out: React.ReactNode[] = [];
  blocks.forEach((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return;
    if (trimmed.startsWith('**') && trimmed.endsWith('**') && !trimmed.slice(2, -2).includes('**')) {
      out.push(<h3 key={`h-${i}`} className="text-lg font-bold text-gray-900 mt-6 mb-2">{stripStars(trimmed)}</h3>);
      return;
    }
    const lines = block.split('\n').filter(Boolean);
    const listLines = lines.filter((l) => l.trim().startsWith('- '));
    if (listLines.length > 0) {
      const intro = lines.filter((l) => !l.trim().startsWith('- ')).join(' ').trim();
      if (intro) out.push(<p key={`p-${i}`} className="text-gray-700 leading-relaxed mb-2">{stripStars(intro)}</p>);
      out.push(
        <ul key={`ul-${i}`} className="list-disc pl-6 my-4 space-y-1 text-gray-700">
          {listLines.map((line, j) => (
            <li key={j}>{stripStars(line.replace(/^-\s*/, ''))}</li>
          ))}
        </ul>
      );
      return;
    }
    out.push(<p key={`p-${i}`} className="text-gray-700 leading-relaxed mb-4">{stripStars(trimmed)}</p>);
  });
  return out;
}

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  // Dynamic SEO
  useSEO({
    title: post?.title,
    description: post?.excerpt,
    canonical: `/blog/${slug}`,
    ogImage: post?.img,
    schema: post ? {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "headline": post.title,
          "image": [post.img],
          "datePublished": post.date,
          "author": [{
              "@type": "Organization",
              "name": "Manlab Medicals",
              "url": "https://www.manlab.co.zw"
          }],
          "description": post.excerpt
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.manlab.co.zw/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Blog",
              "item": "https://www.manlab.co.zw/blog"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": post.title,
              "item": `https://www.manlab.co.zw/blog/${slug}`
            }
          ]
        }
      ]
    } : undefined
  });

  if (!post) {
    return (
      <main className="pt-20 min-h-screen bg-gray-50">
        <div className="container-main py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Post not found</h1>
          <Link to="/" className="text-primary font-semibold hover:underline">Back to Home</Link>
        </div>
      </main>
    );
  }

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = encodeURIComponent(post.title);

  return (
    <main className="pt-20 min-h-screen bg-gray-100">
      <div className="container-main py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Left: Article */}
          <article className="lg:col-span-8 animate-fade-in">
            <header className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4">
                {post.title}
              </h1>
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  {post.author.charAt(0)}
                </span>
                <span>{post.author}</span>
                <span className="text-gray-300">·</span>
                <span>{post.category}</span>
                <span className="text-gray-300">·</span>
                <time dateTime={post.date}>{post.date}</time>
              </div>
            </header>

            <div className="rounded-2xl overflow-hidden shadow-lg mb-8 animate-slide-up">
              <img
                src={post.img}
                alt={post.title}
                className="w-full aspect-video object-cover"
              />
            </div>

            <div className="text-base animate-slide-up animation-delay-100 prose prose-gray max-w-none">
              {formatBody(post.body)}
            </div>

            </article>

          {/* Right: Sidebar */}
          <aside className="lg:col-span-4 space-y-8 animate-fade-in animation-delay-150">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-28">
              <h3 className="font-bold text-gray-900 mb-4">Share to</h3>
              <div className="flex flex-wrap gap-3">
                {SHARE_LINKS.map((s) => (
                  <a
                    key={s.name}
                    href={s.name === 'Email' ? `mailto:?subject=${shareTitle}&body=${shareUrl}` : `${s.href}?url=${encodeURIComponent(shareUrl)}&text=${shareTitle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-primary hover:text-white flex items-center justify-center transition-all duration-200 hover:scale-110"
                    aria-label={`Share on ${s.name}`}
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                      <path d={s.icon} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-primary rounded-2xl p-6 text-white overflow-hidden relative group">
              <div className="relative z-10">
                <h3 className="font-bold text-lg mb-2">Need medical equipment?</h3>
                <p className="text-white/80 text-sm mb-6">Browse our full range of CE-certified laboratory and hospital supplies.</p>
                <Link to="/shop" className="inline-flex items-center justify-center w-full py-3 bg-white text-primary rounded-xl font-bold hover:bg-accent hover:text-white transition-all">
                  Shop All Products
                </Link>
              </div>
              <ScienceIcon className="absolute -right-4 -bottom-4 text-white/10 text-8xl rotate-12 group-hover:rotate-0 transition-transform duration-500" />
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default BlogDetail;
